import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, CalendarClock, Loader2, ShieldCheck, AlertTriangle, RotateCcw, XCircle, LifeBuoy, Send } from "lucide-react";

const STATUS_STYLE = {
  active: { bg: "#ECFDF5", border: "#10B981", text: "#047857", label: "Active" },
  trialing: { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8", label: "Free Trial" },
  past_due: { bg: "#FEF2F2", border: "#EF4444", text: "#B91C1C", label: "Past Due" },
  canceled: { bg: "#F3F4F6", border: "#9CA3AF", text: "#4B5563", label: "Canceled" },
  none: { bg: "#FFFBEB", border: "#F59E0B", text: "#B45309", label: "No Membership" },
};

export default function Account() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loader label="Loading your membership…" />;

  const st = STATUS_STYLE[data.status] || STATUS_STYLE.none;

  const cancel = async () => {
    setBusy(true);
    try { const r = await api.post("/billing/cancel"); toast.success(`Membership will end on ${r.data.ends || "period end"}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/billing/resume"); toast.success("Membership resumed — it will keep renewing"); await load(); }
    catch (e) { toast.error("Could not resume"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="module-account">
      <PageHeader eyebrow="Your Account" title="Account & Billing"
        subtitle="Manage your WhatsMyTribe Heritage Portal membership, see your next charge, and cancel anytime." />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#2A1442] grid place-items-center"><CreditCard size={20} className="text-[#D4AF37]" /></div>
              <div>
                <div className="font-serif text-xl font-semibold text-[#180D24]">{data.plan}</div>
                <div className="text-sm text-[#756D7E]">{data.amount} / {data.interval}</div>
              </div>
            </div>
            <span data-testid="billing-status" className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
              style={{ backgroundColor: st.bg, borderColor: st.border, color: st.text }}>
              <CheckCircle2 size={13} /> {st.label}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-[#FAF8F5] border border-[#E4DCD3] p-4">
              <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1 flex items-center gap-1.5"><CalendarClock size={13} /> {data.status === "trialing" ? "Trial ends" : "Next charge"}</div>
              <div className="text-lg font-semibold text-[#180D24]" data-testid="billing-next-charge">
                {data.cancel_at_period_end ? "Ends " : ""}{data.trial_end || data.next_charge || "—"}
              </div>
            </div>
            <div className="rounded-xl bg-[#FAF8F5] border border-[#E4DCD3] p-4">
              <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1">One-time Research Fee</div>
              <div className="text-lg font-semibold text-[#180D24]">{data.research_fee_paid ? "$20.00 paid" : "Not applicable"}</div>
            </div>
          </div>

          {data.cancel_at_period_end && (
            <div className="mt-4 rounded-xl bg-[#FEF2F2] border border-[#EF4444]/30 p-4 text-sm text-[#B91C1C] flex items-center gap-2">
              <AlertTriangle size={16} /> Your membership is set to cancel at the end of the current period.
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {data.has_subscription && !data.cancel_at_period_end && (
              <button data-testid="cancel-membership-btn" onClick={cancel} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full border border-[#EF4444]/40 text-[#B91C1C] px-5 py-2.5 text-sm font-semibold hover:bg-[#FEF2F2] transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} Cancel Membership
              </button>
            )}
            {data.has_subscription && data.cancel_at_period_end && (
              <button data-testid="resume-membership-btn" onClick={resume} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Resume Membership
              </button>
            )}
          </div>
        </Panel>

        <Panel className="p-6">
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-3">Account</div>
          <div className="space-y-3 text-sm">
            <div><div className="text-[#756D7E] text-xs">Name</div><div className="text-[#180D24] font-medium">{data.name}</div></div>
            <div><div className="text-[#756D7E] text-xs">Email</div><div className="text-[#180D24] font-medium">{data.email}</div></div>
            <div><div className="text-[#756D7E] text-xs">Path</div><div className="text-[#180D24] font-medium capitalize">{data.path || "member"}</div></div>
          </div>
          <div className="hp-gold-rule my-5" />
          <div className="flex items-start gap-2 text-[11px] text-[#756D7E]">
            <ShieldCheck size={24} className="text-[#59288E] shrink-0 -mt-0.5" />
            <span>Your $5/month membership renews until canceled. The one-time $20 research fee is not recurring. Payments are securely processed by Stripe.</span>
          </div>
        </Panel>
      </div>

      <SupportTickets />
    </div>
  );
}

function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState({});
  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data.tickets)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please add a subject and a message"); return; }
    await api.post("/support/tickets", { subject, message });
    setSubject(""); setMessage(""); toast.success("Support ticket submitted — we'll reply here");
    load();
  };
  const sendReply = async (id) => {
    const msg = (reply[id] || "").trim(); if (!msg) return;
    await api.post(`/support/tickets/${id}/reply`, { message: msg });
    setReply((r) => ({ ...r, [id]: "" })); load();
  };

  return (
    <div className="mt-8" data-testid="support-section">
      <div className="flex items-center gap-2 mb-4"><LifeBuoy size={20} className="text-[#59288E]" /><h2 className="font-serif text-2xl font-bold text-[#180D24]">Support</h2></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6">
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-3">Open a new support ticket</div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ticket-subject" placeholder="Subject"
            className="w-full rounded-lg border border-[#E4DCD3] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="ticket-message" placeholder="How can we help?"
            className="w-full rounded-lg border border-[#E4DCD3] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
          <button onClick={submit} data-testid="ticket-submit" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-5 py-2.5 text-sm font-bold hover:bg-[#E7C965] transition-colors"><Send size={15} /> Submit Ticket</button>
        </Panel>
        <Panel className="p-6">
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-3">Your tickets</div>
          {tickets.length === 0 ? <div className="text-sm text-[#756D7E] py-6 text-center">No tickets yet.</div> : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {tickets.map((t) => (
                <div key={t.id} className="rounded-xl border border-[#E4DCD3] p-4" data-testid={`my-ticket-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-serif font-semibold text-[#180D24]">{t.subject}</div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${t.status === "answered" ? "bg-[#E7F6EC] text-[#166534]" : "bg-[#FEF3C7] text-[#B45309]"}`}>{t.status}</span>
                  </div>
                  <div className="space-y-1.5 mt-2">
                    {t.messages.map((m, i) => (
                      <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m.from === "admin" ? "bg-[#F3EEFA] text-[#2A1442]" : "bg-[#FAF8F5] text-[#1A171D]"}`}>
                        <span className="text-[10px] font-bold uppercase text-[#8F7220] mr-1">{m.from === "admin" ? "Support" : "You"}:</span>{m.text}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input value={reply[t.id] || ""} onChange={(e) => setReply((r) => ({ ...r, [t.id]: e.target.value }))} data-testid={`my-ticket-reply-${t.id}`}
                      placeholder="Reply…" className="flex-1 rounded-lg border border-[#E4DCD3] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
                    <button onClick={() => sendReply(t.id)} data-testid={`my-ticket-send-${t.id}`} className="rounded-full bg-[#2A1442] text-white px-3 py-1.5 text-sm font-bold hover:bg-[#482276]"><Send size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
